import * as React from 'react';
import { useAtom } from 'jotai';
import { articlePayloadAtom } from '../../../components/CreateArticle/atom';
import Button from '../../../components/Button';
import { profileAtom } from '../../../components/Settings/atom';
import AvatarDefault from '../../../assets/medium_default_optimized.png';
import ShareIcon from '../../../assets/share-gray-icon.svg';
import SaveIcon from '../../../assets/save-gray-icon.svg';
import ThreedotIcon from '../../../assets/meatball-icon.svg';
import CommentIcon from '../../../assets/comment-article-icon.svg';
import LogoTwygs from '../../../assets/twyg-logo-icon.svg';
import ArrowDownWhiteIcon from '../../../assets/arrow-down-white-icon.svg';
import VerifiedIcon from '../../../assets/verified-icon.svg';
import FoundingCreatorIcon from '../../../assets/founding_creator_icon.png';
import ReactStackIcon from '../../../assets/react-stack.svg';
import FactCheckIcon from '../../../assets/fact-check-icon.svg';
import TableContentMenuIcon from '../../../assets/table-contents-menu-icon.svg';
import TwygsBotIcon from '../../../assets/twygsbot-icon.svg';
import PointIcon from '../../../assets/point-icon.svg';
import { contributorAtom } from '../../../components/Common/Form/textarea-custom-hook-form/atom.js';
import { openAtom } from '../plugins/TableOfContentsPlugin/atom';
import useTruncatedElement from '../../../hooks/useTruncatedElement';
import { TooltipComponent } from '../../../components/Tooltip';
import PlusGreenIcon from '../../../assets/plus-bold-icon.svg';
import RankBadge from '../../../components/RankBadge';

const AuthorProfile = ({
  profile,
  isDesktop,
  readMoreRef,
  isTruncated,
  isReadingMore,
  toggleIsShowingMore
}) => (
  <div className="flex items-center md:items-start gap-3">
    <img
      src={profile.imgUrl || AvatarDefault}
      className={`${isDesktop ? 'w-[84px] h-[84px]' : 'w-[40px] h-[40px]'} flex-shrink-0 rounded-full object-cover`}
      alt="Profile"
    />
    <div className="flex-grow">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className={`${isDesktop ? 'text-xl' : 'text-sm'} font-[600] text-01-jet-black`}>
          {profile.firstName}
        </h1>
        {profile.isVerifiedAuthor && <img src={VerifiedIcon} alt="Verified" />}
        {profile.isFoundingCreator && (
          <img
            src={FoundingCreatorIcon}
            alt="Founding Creator"
            title="Founding Creator"
            className="w-6 h-6"
          />
        )}
        {profile.username && (
          <span className="font-[600] text-xs md:text-lg text-02-dim-gray">
            @{profile.username}
          </span>
        )}
        <div className="px-2 py-1 rounded-[80px] bg-growth-green-50 text-sm text-growth-green-900 font-[600]">
          You
        </div>
        <RankBadge rank={profile?.currentRank} textSize="text-xs md:text-sm" />
      </div>
      <div className="hidden md:!block">
        <p
          ref={readMoreRef}
          className={`${!isReadingMore && 'line-clamp-[10]'} ${
            isDesktop ? 'text-sm' : 'text-xs'
          } mt-2 whitespace-pre-wrap text-01-jet-black`}
        >
          {profile.description || 'No description'}
        </p>
        {isTruncated && (
          <div onClick={toggleIsShowingMore} className="cursor-pointer">
            <p className="text-sm font-[900] text-growth-green-500 hover:opacity-80 mt-[8px]">
              {isReadingMore ? 'Show less' : 'Read more'}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const Contributors = ({ contributors, isDesktop }) => {
  const [showAll, setShowAll] = React.useState(false);

  if (!contributors?.length) return null;

  const maxVisibleContributors = 6;
  const hasMoreContributors = contributors.length > maxVisibleContributors;
  const visibleContributors = showAll
    ? contributors
    : contributors.slice(0, maxVisibleContributors);

  return (
    <div className={`${isDesktop ? 'mt-4 flex gap-4' : 'mt-1'}`}>
      <p className="text-sm md:text-base font-[800] text-01-jet-black">Contributors:</p>
      <div className="flex items-center flex-wrap gap-2 mt-2 md:mt-0">
        {visibleContributors.map((contributor) => (
          <div key={contributor.id} className="flex items-center gap-2">
            <img
              src={contributor.imgUrl || AvatarDefault}
              className="w-6 h-6 flex-shrink-0 rounded-full object-cover"
              alt={contributor.firstName}
            />
            <p className="font-[600] text-sm text-01-jet-black">{contributor.firstName}</p>
          </div>
        ))}
        {hasMoreContributors && !showAll && (
          <button
            onClick={() => setShowAll(true)}
            className="text-growth-green-500 font-[900] text-sm hover:opacity-80"
          >
            View more
          </button>
        )}
        {showAll && (
          <button
            onClick={() => setShowAll(false)}
            className="text-growth-green-500 font-[900] text-sm hover:opacity-80"
          >
            Hide
          </button>
        )}
      </div>
    </div>
  );
};
const TopicTags = ({ topics }) => {
  if (!topics?.length) {
    return <p className="text-earth-brown-500 text-base">No Categories</p>;
  }

  return (
    <div className="flex items-center flex-wrap gap-2">
      {topics.slice(0, 4).map((topic) => (
        <p
          key={topic.id}
          className="bg-[#F0EDEC] rounded-[80px] !text-earth-brown-500 font-semibold text-xs md:text-sm px-2 py-1 whitespace-nowrap"
        >
          {topic.name}
        </p>
      ))}
    </div>
  );
};

const AISummary = ({ isEnabled, isExpanded, toggleSummary }) => {
  if (!isEnabled) return null;

  return (
    <div className="w-full">
      <div className="flex w-full items-center justify-between px-4 md:px-6 py-2 bg-05-seasalt-white rounded-[16px] shadow-[0px_0px_8px_0px_#00000024]">
        <div className="flex gap-2 items-center">
          <img src={LogoTwygs} className="w-6 h-6" alt="Logo" />
          <p className="text-growth-green-500 text-base md:text-xl font-[800]">Twygs AI Summary</p>
        </div>
        <button
          onClick={toggleSummary}
          className="flex items-center !py-2 px-[14px] text-sm !font-[700] bg-growth-green-500 text-white rounded-md"
        >
          {isExpanded ? 'Hide' : 'Show'}
          <img
            src={ArrowDownWhiteIcon}
            className={`ml-2 w-5 h-5 transform transition-transform duration-300 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            alt="Arrow"
          />
        </button>
      </div>
      <div
        className={`overflow-hidden transition-max-height duration-500 ease-in-out ${
          isExpanded ? 'max-h-[500px] pt-4' : 'max-h-0'
        }`}
      >
        <p className="bg-growth-green-50 text-[#333] text-lg py-4 px-[10px] font-[500] rounded-[16px]">
          AI summary content will be generated and shown here after the article is published
        </p>
      </div>
    </div>
  );
};

interface PreviewArticleProps {
  cover?: string;
  disableTabsContent?: boolean;
}
const PreviewArticleComponent: React.FC<PreviewArticleProps> = ({ cover, disableTabsContent }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [articlePayload] = useAtom(articlePayloadAtom);
  const [profile] = useAtom(profileAtom);
  const [contributors] = useAtom(contributorAtom);
  const [isOpen, setIsOpen] = useAtom(openAtom);
  const readMoreRef1 = React.useRef(null);
  const readMoreRef2 = React.useRef(null);

  const truncatedText1 = useTruncatedElement({
    ref: readMoreRef1
  });
  const truncatedText2 = useTruncatedElement({
    ref: readMoreRef2
  });
  const formatContributors = React.useMemo(
    () =>
      contributors
        .filter((contributor) => contributor.user.id !== profile.id)
        .map((contributor) => contributor.user),
    [contributors, profile.id]
  );

  const sidebarButtons = [
    {
      icon: TableContentMenuIcon,
      bg: 'bg-[#C8E6C9]',
      onClick: () => setIsOpen(!isOpen)
    },
    {
      icon: FactCheckIcon,
      bg: 'bg-[#C8E6C9]',
      disabled: true,
      type: 'fact-check',
      hasToolTip: true,
      textTooltip:
        'AI Fact Checker: The reader will be able to access fact checks made by them from here'
    },
    {
      icon: TwygsBotIcon,
      bg: 'bg-[#C8E6C9]',
      disabled: true,
      type: 'twygs-bot',
      hasToolTip: true,
      textTooltip:
        'Twygs bot: The reader will be able to ask this bot anything to clarify or explore more about the article.'
    }
  ];

  return (
    <div className="px-4 lg:px-[28px] py-[10px] lg:py-10 mt-10 md:mt-0 flex flex-col gap-6 md:gap-10 font-ui relative max-w-[868px] mx-auto">
      {/* Sidebar */}
      <div
        className={`fixed left-4 md:left-20 w-fit top-[70px] md:top-[100px] z-10 flex flex-row md:flex-col gap-2 ${disableTabsContent && 'hidden'}`}
      >
        {sidebarButtons.map((button, index) => (
          <div
            key={index}
            className={`p-1 md:p-2 rounded-[80px] ${button.bg} transition-transform duration-300 ease-in-out transform hover:scale-105 ${
              button.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            onClick={!button.disabled ? button.onClick : undefined}
          >
            {button.hasToolTip ? (
              <div>
                <TooltipComponent
                  placement="top"
                  maxW="190px"
                  className="w-[17px] h-[17px] md:w-[20px] md:h-[20px]"
                  Icon={button.icon}
                  description={button.textTooltip}
                />
              </div>
            ) : (
              <img className="w-[17px] h-[17px] md:w-[20px] md:h-[20px]" src={button.icon} alt="" />
            )}
          </div>
        ))}
      </div>

      {/* Desktop Profile */}
      <div className="hidden md:!block border-b border-normal-gray pb-4">
        <div className="flex justify-between items-start w-full">
          <div className="flex gap-4 w-[80%]">
            <AuthorProfile
              profile={profile}
              isDesktop={true}
              readMoreRef={readMoreRef1}
              isTruncated={truncatedText1.isTruncated}
              isReadingMore={truncatedText1.isReadingMore}
              toggleIsShowingMore={truncatedText1.toggleIsShowingMore}
            />
          </div>
          <div className="flex gap-6 w-[20%] items-start justify-end">
            <img src={ThreedotIcon} alt="" />
          </div>
        </div>
        <Contributors contributors={formatContributors} isDesktop />
      </div>

      {/* Mobile Profile */}
      <div className="flex md:!hidden flex-col gap-2 border-b border-normal-gray pb-2">
        <div className="flex justify-between items-center">
          <AuthorProfile
            profile={profile}
            isDesktop={false}
            readMoreRef={readMoreRef2}
            isTruncated={truncatedText2.isTruncated}
            isReadingMore={truncatedText2.isReadingMore}
            toggleIsShowingMore={truncatedText2.toggleIsShowingMore}
          />
          <div className="flex gap-2 items-start">
            <Button className="!py-2 px-[14px] text-sm !font-[700]">Follow</Button>
            <img src={ThreedotIcon} alt="" />
          </div>
        </div>
        <div className="block md:hidden">
          <p
            ref={readMoreRef2}
            className={`${!truncatedText2.isReadingMore && 'line-clamp-[10]'} text-sm whitespace-pre-wrap text-01-jet-black`}
          >
            {profile.description || 'No description'}
          </p>
          {truncatedText2.isTruncated && (
            <div onClick={truncatedText2.toggleIsShowingMore} className="cursor-pointer">
              <p className="text-sm font-[900] text-growth-green-500 hover:opacity-80 mt-[8px]">
                {truncatedText2.isReadingMore ? 'Show less' : 'Read more'}
              </p>
            </div>
          )}
        </div>
        <Contributors contributors={formatContributors} isDesktop={false} />
      </div>

      {/* Article Content */}
      <div className="flex flex-col gap-2 md:gap-4">
        <p className="text-[24px] md:text-[32px] font-[800] text-01-jet-black">
          {articlePayload?.title || 'Untitled'}
        </p>
        <p className="text-01-jet-black md:text-base">
          {articlePayload?.description || 'No description'}
        </p>
        <TopicTags topics={articlePayload?.topicIds} />

        <div className="flex justify-between items-center">
          <div className="flex">
            <div className="flex items-center gap-2">
              <img className="h-4 md:h-5" src={ReactStackIcon} alt="" />
              <p className="font-[500] text-sm 2xl:text-base">0</p>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <img className="w-4 h-4 md:w-5 md:h-5" src={CommentIcon} alt="" />
              <p className="font-[500] text-sm 2xl:text-base">0</p>
            </div>
            <div className="flex ml-4 gap-1 items-center py-1 px-2 border border-[#C8E6C9] rounded-[80px]">
              <img className="w-4 h-4 md:w-5 md:h-5" src={PointIcon} alt="" />
              <img className="w-4 h-4 md:w-5 md:h-5" src={PlusGreenIcon} alt="" />
            </div>
          </div>
          <div className="flex items-start gap-2 md:gap-4">
            <img className="w-4 h-4 md:w-5 md:h-5" src={ShareIcon} alt="" />
            <img className="w-4 h-4 md:w-5 md:h-5" src={SaveIcon} alt="" />
            <img className="w-4 h-4 md:w-5 md:h-5" src={ThreedotIcon} alt="" />
          </div>
        </div>
      </div>

      {/* Cover Image */}
      {cover || articlePayload?.cover ? (
        <img
          src={cover || articlePayload.cover}
          className="rounded-[13.4px] w-full h-[201px] md:h-[457px] object-cover"
          alt="Cover"
        />
      ) : (
        <div className="w-full h-[201px] md:h-[457px] rounded-[12px] bg-05-seasalt-white flex items-center justify-center">
          <p>No image</p>
        </div>
      )}

      {/* AI Summary Section */}
      <AISummary
        isEnabled={articlePayload?.isEnableAISummaries !== false}
        isExpanded={isExpanded}
        toggleSummary={() => setIsExpanded(!isExpanded)}
      />
    </div>
  );
};

export default PreviewArticleComponent;
